import React, { Component } from 'react';
import authService from '../../services/authService';
import office365Service from '../../services/office365Service';
import logo from '../../../assets/logo.png';

// Custom Microsoft Icon component since it's not in Heroicons
const MicrosoftIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"/>
  </svg>
);

class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      office365Loading: false,
      error: null,
      validated: false,
      usernameError: '',
      passwordError: '',
      showOffice365: false,
    };
  }

  componentDidMount() {
    // Check if Office 365 SSO is available
    this.checkOffice365Availability();
  }

  checkOffice365Availability = () => {
    try {
      // Check if Office 365 SSO is available
      const showOffice365 = office365Service.isSSoAvailable();

      this.setState({ showOffice365 });
    } catch (error) {
      this.setState({ showOffice365: false });
    }
  };

  validateField = (name, value) => {
    let error = '';
    
    if (name === 'username') {
      if (!value || value.trim() === '') {
        error = 'Username is required';
      }
    } else if (name === 'password') {
      if (!value || value.trim() === '') {
        error = 'Password is required';
      }
    }
    
    return error;
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    const error = this.validateField(name, value);
    
    this.setState({
      [`${name}Error`]: error
    });
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData(event.target);
    const username = formData.get('username');
    const password = formData.get('password');

    // Validate both fields
    const usernameError = this.validateField('username', username);
    const passwordError = this.validateField('password', password);

    this.setState({
      usernameError,
      passwordError,
      validated: true
    });

    // If there are validation errors, don't proceed
    if (usernameError || passwordError) {
      return;
    }

    this.setState({ error: null, loading: true });

    try {
      const result = await authService.login(username, password);

      if (!result.success) {
        this.setState({ error: result.error, loading: false });
      }
    } catch (error) {
      this.setState({ error: 'Login failed. Please try again.', loading: false });
    }
  };

  handleOffice365Login = async () => {
    this.setState({ office365Loading: true, error: null });

    try {
      const result = await authService.loginWithOffice365();

      if (!result.success) {
        this.setState({
          error: result.error,
          office365Loading: false
        });
      }
    } catch (error) {
      this.setState({
        error: error.message,
        office365Loading: false
      });
    }
  };

  render() {
    const {
      loading,
      office365Loading,
      error,
      usernameError,
      passwordError,
      showOffice365
    } = this.state;

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-center mb-6">
                <img src={logo} className="w-1/2" alt="Revit CRM Logo" />
              </div>

              <div className="text-center mb-6">
                <h1 className="text-xl font-semibold text-gray-900 mb-2">
                  Sign in to your account
                </h1>
                
              </div>

              {/* Office 365 SSO Button - Only show if available */}
              {showOffice365 && (
                <>
                  <div className="mb-6">
                    <button
                      type="button"
                      onClick={this.handleOffice365Login}
                      disabled={office365Loading || loading}
                      className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {office365Loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                          <span>Signing in with Office 365...</span>
                        </>
                      ) : (
                        <>
                          <MicrosoftIcon className="w-5 h-5 text-blue-600" />
                          <span>Sign in with Office 365</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Or continue with</span>
                    </div>
                  </div>
                </>
              )}

              {/* Username/Password Form */}
              <form className="space-y-4" onSubmit={this.handleSubmit}>
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    id="username"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      usernameError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    type="text"
                    placeholder="Enter your username"
                    name="username"
                    disabled={loading || office365Loading}
                    onChange={this.handleInputChange}
                  />
                  {usernameError && (
                    <p className="text-xs text-red-600 mt-1">{usernameError}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    id="password"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      passwordError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    type="password"
                    placeholder="Enter your password"
                    name="password"
                    disabled={loading || office365Loading}
                    onChange={this.handleInputChange}
                  />
                  {passwordError && (
                    <p className="text-xs text-red-600 mt-1">{passwordError}</p>
                  )}
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <button
                  className="w-full flex items-center justify-center gap-2 text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  style={{ backgroundColor: '#0075a8' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#005e86'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0075a8'}
                  disabled={loading || office365Loading}
                  type="submit"
                >
                  {loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  <span>{loading ? 'Signing in...' : 'Sign In'}</span>
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  Powered by Revit CRM
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Login;